import { createI18n } from 'vue-i18n';

import {
  type IApp,
  type IPageLang,
  type GetMsgFunction,
  type i18nMessage,
  type LangCodes,
  isLocaleAllowed,
} from './i18n.types';

import { getBaseMsgs, getPageMsgs } from './localTranslation';

const buildLoadFunction = function getLoadFunction(
  loadBaseMsg: GetMsgFunction,
  loadPageMsgs: GetMsgFunction,
): GetMsgFunction {
  return async function loadMsg(options: IPageLang): Promise<i18nMessage> {
    const baseMsgs = await loadBaseMsg(options);
    const pageMsgs = await loadPageMsgs(options);

    return {
      ...baseMsgs,
      [options.pageName]: pageMsgs,
    };
  };
};

const loadMsgs: GetMsgFunction = buildLoadFunction(getBaseMsgs, getPageMsgs);

const DEFAULT_LANG: LangCodes = 'en';

export const i18n = createI18n({
  legacy: false,
  locale: DEFAULT_LANG,
  fallbackLocale: DEFAULT_LANG,
});

export async function setupI18n({ vueApp, pageName, router }: IApp) {
  const defaultMsgs = await loadMsgs({ pageName, langCode: DEFAULT_LANG });

  i18n.global.setLocaleMessage(DEFAULT_LANG, defaultMsgs);

  const routeLocale = (router.currentRoute.value.params?.lang ??
    DEFAULT_LANG) as string;

  const currentLocale: LangCodes = isLocaleAllowed(routeLocale)
    ? routeLocale
    : DEFAULT_LANG;

  if (currentLocale !== DEFAULT_LANG) {
    try {
      const localeMsgs = await loadMsgs({
        pageName,
        langCode: currentLocale,
      });

      i18n.global.setLocaleMessage(currentLocale, localeMsgs);
      i18n.global.locale.value = currentLocale;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn(error);
    }
  }

  vueApp.use(i18n);
}
